/**
 * TASK-VIS-003: Ability Popup Manager
 *
 * Shows floating text popups when abilities trigger.
 * Text floats up and fades out over time.
 */

import * as PIXI from 'pixi.js';

export interface AbilityPopup {
  id: string;
  text: PIXI.Text;
  startTime: number;
  startY: number;
}

export class AbilityPopupManager {
  private popups: Map<string, AbilityPopup> = new Map();
  private container: PIXI.Container;

  // Animation settings
  private readonly POPUP_DURATION_MS = 1200;
  private readonly FLOAT_DISTANCE = 50; // pixels to float up
  private readonly FONT_SIZE = 18;

  // Damage type colors
  private readonly COLORS: Record<string, number> = {
    physical: 0xFFFFFF,
    ice: 0x4FC3F7,
    fire: 0xFF7043,
    lightning: 0xFFEB3B,
    dark: 0x9C27B0,
    poison: 0x4CAF50,
    holy: 0xFFF59D,
    default: 0xFFFFFF
  };

  constructor(parentContainer: PIXI.Container) {
    this.container = new PIXI.Container();
    this.container.sortableChildren = true;
    this.container.zIndex = 1000; // Above creatures
    parentContainer.addChild(this.container);
  }

  /**
   * Show ability name popup at position
   */
  public showPopup(abilityName: string, x: number, y: number, damageType?: string): void {
    const id = `popup_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Format ability name nicely (ice_spell -> Ice Spell)
    const displayName = this.formatAbilityName(abilityName);

    // Get color for damage type
    const color = this.COLORS[damageType || 'default'] || this.COLORS.default;

    // Create text (PIXI.js v8 API)
    const text = new PIXI.Text({
      text: displayName,
      style: {
        fontFamily: 'Arial Black, Arial',
        fontSize: this.FONT_SIZE,
        fontWeight: 'bold',
        fill: color,
        stroke: { color: 0x000000, width: 4 },
        align: 'center'
      }
    });

    // Center the text
    text.anchor.set(0.5, 1);
    text.x = x;
    text.y = y - 30; // Start above the position
    text.alpha = 0;
    text.scale.set(0.5);

    this.container.addChild(text);

    const popup: AbilityPopup = {
      id,
      text,
      startTime: Date.now(),
      startY: text.y
    };

    this.popups.set(id, popup);

    console.log(`[AbilityPopupManager] Showing popup: "${displayName}" at (${x.toFixed(0)}, ${y.toFixed(0)})`);
  }

  /**
   * Format ability ID to display name
   * ice_spell -> Ice Spell
   */
  private formatAbilityName(abilityId: string): string {
    return abilityId
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Update all popups - call in render loop
   */
  public update(): void {
    const now = Date.now();
    const toRemove: string[] = [];

    this.popups.forEach((popup, id) => {
      const elapsed = now - popup.startTime;
      const progress = Math.min(1, elapsed / this.POPUP_DURATION_MS);

      if (progress >= 1) {
        toRemove.push(id);
        return;
      }

      // Easing function for smooth animation
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const easeInOut = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      // Float up
      popup.text.y = popup.startY - (this.FLOAT_DISTANCE * easeOut);

      // Scale: pop in then stay
      if (progress < 0.15) {
        const scaleProgress = progress / 0.15;
        popup.text.scale.set(0.5 + (0.6 * scaleProgress)); // 0.5 -> 1.1
      } else if (progress < 0.25) {
        const scaleProgress = (progress - 0.15) / 0.1;
        popup.text.scale.set(1.1 - (0.1 * scaleProgress)); // 1.1 -> 1.0
      } else {
        popup.text.scale.set(1);
      }

      // Alpha: fade in quick, stay, fade out
      if (progress < 0.1) {
        popup.text.alpha = progress / 0.1;
      } else if (progress < 0.7) {
        popup.text.alpha = 1;
      } else {
        popup.text.alpha = 1 - ((progress - 0.7) / 0.3);
      }
    });

    // Remove expired popups
    toRemove.forEach(id => {
      const popup = this.popups.get(id);
      if (popup) {
        this.container.removeChild(popup.text);
        popup.text.destroy();
        this.popups.delete(id);
      }
    });
  }

  /**
   * Clear all popups
   */
  public clear(): void {
    this.popups.forEach(popup => {
      this.container.removeChild(popup.text);
      popup.text.destroy();
    });
    this.popups.clear();
  }

  /**
   * Destroy manager
   */
  public destroy(): void {
    this.clear();
    this.container.destroy();
  }
}
